import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/auth";
import PermissionGuard from "./PermissionGuard";
import ConfirmDialog from "./ConfirmDialog";

const Role = () => {
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, roleId: null, roleName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    let isMounted = true;
    const source = axios.CancelToken.source();

    const fetchRoles = async () => {
      try {
        setLoading(true);
        setError("");

        if (!isAuthenticated || !token) {
          setError("Please login to view roles");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${baseUrl}/auth/roles`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: source.token,
        });

        if (isMounted) {
          setRoles(response.data);
        }
      } catch (error) {
        if (isMounted && !axios.isCancel(error)) {
          console.error("Error fetching roles:", error);
          if (error.response?.status === 401) {
            setError("Authentication failed. Please login again.");
          } else {
            setError("Failed to load roles. Please try again.");
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRoles();

    return () => {
      isMounted = false;
      source.cancel("Component unmounted. Request canceled.");
    };
  }, [baseUrl, token, isAuthenticated]);

  const handleAdd = () => navigate("/dashboard/addRole");
  const handleEdit = (id) => navigate(`/dashboard/updateRoles/${id}`);

  const handleOpenDialog = (id) => {
    const role = roles.find(r => r.id === id);
    if (!role) return;

    setDeleteDialog({
      open: true,
      roleId: id,
      roleName: role.name
    });
  };

  const handleCloseDialog = () => {
    setDeleteDialog({ open: false, roleId: null, roleName: '' });
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${baseUrl}/auth/roles/${deleteDialog.roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setRoles(roles.filter((role) => role.id !== deleteDialog.roleId));
      handleCloseDialog();
    } catch (error) {
      console.error("Error deleting role:", error);
      setError(error.response?.data?.error || "Failed to delete role. Please try again.");
      setDeleteLoading(false);
    }
  };

  const filteredRoles = roles.filter((role) => {
    const searchLower = searchTerm.toLowerCase();
    return role.name?.toLowerCase().includes(searchLower);
  });

  if (!isAuthenticated) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Role Management
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          Please login to view roles.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/login")}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Role Management
        </Typography>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Role Management
        </Typography>
        <PermissionGuard resource="role" action="create">
          <Button variant="contained" onClick={handleAdd} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Add Role
          </Button>
        </PermissionGuard>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Search roles..."
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiInputBase-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          },
          '& .MuiInputLabel-root': {
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }
        }}
      />

      {filteredRoles.length === 0 && !loading ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {roles.length === 0
            ? "No roles found. Add your first role to get started."
            : "No roles match your search criteria."}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 650, md: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1976d2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Role Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{role.name || "N/A"}</TableCell>
                  <TableCell>
                    <PermissionGuard resource="role" action="edit">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(role.id)}
                        title="Edit Role"
                      >
                        <EditIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                    <PermissionGuard resource="role" action="delete">
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDialog(role.id)}
                        title="Delete Role"
                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                      </IconButton>
                    </PermissionGuard>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
        title="Delete Role"
        message={`Are you sure you want to delete the "${deleteDialog.roleName}" role?`}
        warning="⚠️ Deleting a role will affect all users assigned to this role. They will lose their role-based permissions."
        severity="warning"
        loading={deleteLoading}
        confirmText="Delete Role"
        confirmColor="error"
      />
    </Container>
  );
};

export default Role;
